import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, InfoOutlineIcon, RawButton, Text } from '@common-fe/common-fe';
import dayjs from 'dayjs';

import { DEFAULT_DATE_FORMAT } from '@/common/constants';
import { useGetAccountOrdersQuery, useGetEnrollmentsQuery, useGetPolicyServicesQuery } from '@/modules/UseMyMoney/queries';
import useEmployeeStore from '@/modules/user/stores/useEmployee.store';

import AccountDetailsPopup from './components/AccountDetailsPopup/AccountDetailsPopup';
import AccountsList from './components/AccountsList/AccountsList';
import useCombinedAccountData from './hooks/useCombinedAccountData';

interface Props {
  isMobile?: boolean;
}

const AccountsDetailsContainer: React.FC<Props> = ({ isMobile }) => {
  const { t } = useTranslation();
  const { employee } = useEmployeeStore();
  const [isAccountDetailsPopupVisible, setIsAccountDetailsPopupVisible] = useState(false);

  const { activeEnrollments, isEnrollmentsLoading } = useGetEnrollmentsQuery({
    employeeId: employee?.id
  });

  const { uniqAccounts, isAccountOrdersLoading } = useGetAccountOrdersQuery({
    employeeId: employee?.id,
    serviceOn: dayjs().format(DEFAULT_DATE_FORMAT),
  });

  const policyIds = useMemo(() => {
    const policyIds = activeEnrollments?.map((enrollment) => enrollment?.policyId)?.filter(Boolean) as string[] || [];
    const uniqPolicyIds = [...new Set(policyIds)];
    return uniqPolicyIds;
  }, [activeEnrollments]);

  const { policyEligibleServices, isPolicyServicesLoading } = useGetPolicyServicesQuery({
    policyIds,
  });

  const isLoading = useMemo(() => isEnrollmentsLoading || isAccountOrdersLoading || isPolicyServicesLoading,
    [isEnrollmentsLoading, isAccountOrdersLoading, isPolicyServicesLoading]);
  
  const { combinedAccountsData } = useCombinedAccountData({
    accounts: uniqAccounts,
    enrollments: activeEnrollments,
    policyEligibleServices,
    isLoading,
  });

  const handleOpenAccountDetailsPopup = useCallback(() => {
    setIsAccountDetailsPopupVisible(true);
  }, []);

  return (
    <>
      {isMobile ? (
        <RawButton
          data-testId="account-details"
          onClick={handleOpenAccountDetailsPopup}
        >
          <Box
            direction="row"
            align="center"
            gap="spacing4"
            round="60px"
            border={{ size: '1px', color: 'border2' }}
            pad={{ horizontal: 'spacing16', vertical: 'spacing12' }}
            height={{ min: '48px' }}
            width={{ min: '162px' }}
          >
            <InfoOutlineIcon color="iconPrimary" size="20px" />
            <Text color="textBody" weight={400}>
              {t('Account Details')}
            </Text>
          </Box>
        </RawButton>
      ) : (
        <Box width="100%">
          <Text color="textSecondary">
            {t('Account details')}
          </Text>
          <Box margin={{ top: 'spacing8' }}>
            <AccountsList
              accountsData={combinedAccountsData}
              isLoading={isLoading}
              onReadMore={handleOpenAccountDetailsPopup}
            />
          </Box>
        </Box>
      )}

      {isAccountDetailsPopupVisible && (
        <AccountDetailsPopup
          accountsData={combinedAccountsData}
          onSetVisible={setIsAccountDetailsPopupVisible}
        />
      )}
    </>
  );
};

export default AccountsDetailsContainer;
